// src/cockpit4/components/FirstRunOnboarding.tsx
// ============================================
// Phase3 P1-2：首次登录新手向导（4 步引导，非阻塞）
//
// 需求（UX §4.2）：新账号首次登录自动弹出，4 步：
//   1. 系统初始化，接入 Ontology 知识源
//   2. 配置获客渠道（GEO+DSO + 自媒体平台授权）
//   3. 跑通内容生产、发布引流
//   4. 线索进入客户中心，跟进成交
// 交互：非阻塞——底部始终有「跳过 / 稍后再说」，可回常规工作台；
//       向导完成自动跳转总控台首页；配置类步骤允许暂存、可稍后补。
//
// 铁律：本地引导状态存 localStorage（不影响 Vault 真实数据）；
//       游客（guest）不触发，避免遮罩层与引导叠加冲突。
// ============================================

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Database, Radar, Factory, Users, X, ArrowRight, Check,
  BookOpen, Megaphone, Sparkles,
} from "lucide-react";
import { useCockpit4Store, type WorkspaceId } from "../store";
import { useUserStore } from "../../stores/userStore";

const SKIP_KEY = "xeaos_first_run_skipped";

interface OnboardingStep {
  title: string;
  desc: string;
  /** 点击该步骤进入的工作区 */
  to: WorkspaceId;
  icon: React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>;
  color: string;
}

const STEPS: OnboardingStep[] = [
  {
    title: "接入知识源",
    desc: "系统初始化，接入 Ontology 企业本体图谱，成为全系统 AI 决策的知识源",
    to: "knowledge",
    icon: Database,
    color: "#22D3EE",
  },
  {
    title: "配置获客渠道",
    desc: "授权 GEO+DSO 挖掘与自媒体平台，让公域流量开始进入系统",
    to: "geodso",
    icon: Radar,
    color: "#3B82F6",
  },
  {
    title: "内容生产与发布",
    desc: "在内容工厂批量生产素材，一键发布到营销平台引流",
    to: "media_studio",
    icon: Factory,
    color: "#A855F7",
  },
  {
    title: "线索跟进成交",
    desc: "线索自动进入客户中心，跟进洽谈、完成成交闭环",
    to: "crm",
    icon: Users,
    color: "#EC4899",
  },
];

export default function FirstRunOnboarding() {
  const [visible, setVisible] = useState(false);
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);
  const currentUser = useUserStore((s) => s.currentUser);

  // 首次登录判定：纯客户端（super_admin/employee 且从未跳过）。游客不弹。
  useEffect(() => {
    try {
      const role = currentUser?.role;
      if (!role || role === "guest") return; // 游客跳过引导
      const skipped = localStorage.getItem(SKIP_KEY);
      if (skipped === "1") return;
      // 延迟弹出，让首屏先渲染
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    } catch {
      /* localStorage 不可用时静默降级 */
    }
  }, [currentUser]);

  const dismiss = (persist: boolean) => {
    setVisible(false);
    if (persist) {
      try { localStorage.setItem(SKIP_KEY, "1"); } catch { /* noop */ }
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="relative w-[440px] max-w-[92vw] rounded-2xl border border-white/10 bg-[#0B0F17]/95 backdrop-blur-xl p-6 shadow-2xl"
          >
            {/* 关闭：仅稍后（不标记已读，下次登录仍提醒） */}
            <button
              onClick={() => dismiss(false)}
              className="absolute top-3 right-3 p-1.5 rounded-md text-gray-500 hover:text-white hover:bg-white/5 transition-colors"
              title="稍后再说"
            >
              <X size={15} />
            </button>

            {/* 标题 */}
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles size={16} className="text-cyan-400" />
                <h2 className="text-[15px] font-semibold text-white tracking-tight">
                  欢迎进入 XEAOS
                </h2>
              </div>
              <p className="text-[11px] text-gray-400 leading-relaxed">
                用 4 步跑通「获客 → 内容 → 成交」业务闭环。每步可稍后补，随时回来。
              </p>
            </div>

            {/* 4 步列表 */}
            <div className="flex flex-col gap-2 mb-5">
              {STEPS.map((s, i) => (
                <button
                  key={s.title}
                  onClick={() => {
                    setWorkspace(s.to);
                    dismiss(false);
                  }}
                  className="group flex items-center gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5 text-left hover:border-white/15 hover:bg-white/[0.04] transition-colors"
                >
                  <div
                    className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ background: `${s.color}18`, color: s.color }}
                  >
                    <s.icon size={15} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-[12px] text-white">
                      <span className="text-[9px] font-mono text-gray-600">{i + 1}.</span>
                      <span>{s.title}</span>
                    </div>
                    <p className="text-[10px] text-gray-500 leading-snug mt-0.5">{s.desc}</p>
                  </div>
                  <ArrowRight
                    size={14}
                    className="text-gray-600 group-hover:text-white transition-colors flex-shrink-0"
                  />
                </button>
              ))}
            </div>

            {/* 完成 / 跳过 */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  setWorkspace("ceo");
                  dismiss(true);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-[11px] font-medium hover:opacity-90 transition-opacity"
              >
                <Check size={12} />
                我已了解，开始使用
              </button>
              <button
                onClick={() => dismiss(true)}
                className="px-3 py-1.5 rounded-md text-[11px] text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
              >
                跳过
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}